import { useMemo } from 'react';

import dayjs from 'dayjs';

import { TimeXAxisProps } from './types';
import { defaultTimeTicksConfig, determineTickConfig } from './utils';
import { useChartContext } from '../../ChartProvider';
import { XAxis } from '../xAxis';

export const TimeXAxis = <T,>({
  timezone = dayjs.tz.guess(),
  unixDataKey,
  ticksConfig,
  dropFirstTick = false,
  ...props
}: TimeXAxisProps<T>) => {
  const { data } = useChartContext<T>();

  const _ticksConfig = useMemo(() => {
    if (!ticksConfig) {
      return defaultTimeTicksConfig;
    }
    return ticksConfig;
  }, [ticksConfig]);

  const { ticks, tickFormatter } = useMemo(() => {
    const tickConfig = determineTickConfig({
      ticksConfig: _ticksConfig,
      chartData: data,
      unixDataKey,
    });

    const ticks = tickConfig.resolveTimeTicks(data, unixDataKey, timezone);
    const formatter = tickConfig.timeTickFormatter;

    return {
      ticks: dropFirstTick ? ticks.slice(1) : ticks,
      tickFormatter: formatter,
    };
  }, [_ticksConfig, data, timezone, unixDataKey, dropFirstTick]);

  return (
    <XAxis
      scale="time"
      domain={['dataMin', 'dataMax']}
      type="number"
      dataKey={unixDataKey}
      interval="equidistantPreserveStart"
      tickFormatter={(t, index) =>
        tickFormatter({ unixTimestamp: Number(t), index, timezone })
      }
      ticks={ticks}
      {...props}
    />
  );
};
